import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Check } from "lucide-react";
import BeforeAfter from "@/components/site/BeforeAfter";
import SEO from "@/components/site/SEO";
import { IMG } from "@/data/media";
import { breadcrumbSchema } from "@/data/site";

const receiveBlocks = [
  { t: "Property photography",   b: "Rooms, features, exteriors, and details captured with a listing-first mindset." },
  { t: "Room + feature capture", b: "Structured documentation of what makes the property worth attention." },
  { t: "Listing detail notes",   b: "Key details, upgrades, and highlights organized for review and use." },
  { t: "MLS-ready exports",      b: "Photo selections formatted and organized for MLS-ready workflows." },
  { t: "Web-ready exports",      b: "Selections prepared for agent and brokerage website listings." },
  { t: "Readiness checklist",    b: "A single view of what is captured, reviewed, and ready to go live." },
];

export default function ListingStandard() {
  return (
    <div className="bg-white text-brand-black">
      <SEO
        title="Listing Standard · Better Property Presentation"
        description="See how LotWise Home helps real estate professionals create cleaner, more complete, and more controlled property listing presentation. Before-and-after proof across rooms, kitchens, exteriors, and more."
        path="/listing-standard"
        schema={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Listing Standard", path: "/listing-standard" }])]}
      />
      {/* Hero */}
      <section className="bg-brand-black text-white">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-24 md:py-32">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="eyebrow text-white/60">Listing Standard · Before / After</div>
              <h1 className="mt-4 text-[40px] md:text-7xl font-bold tracking-[-0.02em] leading-[1.02]">
                Better presentation is visible <span className="text-brand-gradient">before it is explained.</span>
              </h1>
            </div>
            <p className="lg:col-span-4 text-[16px] leading-relaxed text-white/70 max-w-lg">
              A strong listing does not only show the property. It shows that the property has been
              handled properly. Drag any of the sliders below to see the difference.
            </p>
          </div>
        </div>
      </section>

      {/* Before/After grid */}
      <section className="bg-white">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-24 md:py-32">
          <div className="max-w-3xl mb-10 border-l-2 border-brand-blue/60 pl-4">
            <div className="eyebrow-accent">What you are looking at</div>
            <p className="mt-3 text-[14.5px] leading-relaxed text-brand-gray">
              These comparisons show <span className="text-brand-black font-semibold">presentation changes only</span>:
              framing, lighting, composition, and how the property is captured. LotWise Home does not
              perform renovations, construction, or physical staging. Any physical staging would be
              coordinated separately through approved providers.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {IMG.pairs.map((p) => (
              <div key={p.slug}>
                <BeforeAfter
                  before={`/generated/${p.slug}-before.png`}
                  after={`/generated/${p.slug}-after.png`}
                  label={p.label}
                  testId={`ls-before-after-${p.slug}`}
                />
                <div className="mt-3 flex items-center justify-between text-[12px] tracking-[0.18em] uppercase text-brand-gray">
                  <span>{p.label}</span>
                  <span>Drag to compare</span>
                </div>
                <p className="mt-3 text-[12px] leading-relaxed text-brand-gray border-l-2 border-brand-blue/60 pl-3">
                  <span className="text-brand-black font-semibold">LotWise role: </span>
                  photography, framing, composition, and presentation planning.
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What is captured */}
      <section className="bg-[#F7F7F5]">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-24 md:py-32">
          <div className="max-w-3xl">
            <div className="eyebrow-accent">The Standard</div>
            <h2 className="mt-3 text-[34px] md:text-5xl font-bold tracking-tight leading-[1.05]">
              What gets captured. What the agent receives.
            </h2>
            <p className="mt-5 text-[16px] leading-relaxed text-brand-gray">
              A stronger listing starts with better capture, cleaner organization, and a clearer
              standard for what needs to be ready before the property goes live.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {receiveBlocks.map((r, i) => (
              <div key={r.t} className="bg-white border border-brand-gray/25 p-6">
                <div className="flex items-baseline justify-between">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-brand-gray">Item {String(i + 1).padStart(2, "0")}</div>
                  <Check className="h-4 w-4 text-brand-blue" />
                </div>
                <h3 className="mt-4 text-[18px] font-bold tracking-tight">{r.t}</h3>
                <p className="mt-3 text-[13.5px] text-brand-gray leading-relaxed">{r.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Seller / Buyer framing */}
      <section className="bg-white">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-24 md:py-32 grid lg:grid-cols-2 gap-6">
          <div className="border-t-2 border-brand-black pt-8">
            <div className="eyebrow">Seller</div>
            <h3 className="mt-3 text-2xl md:text-3xl font-bold tracking-tight">Sellers notice when their home is handled properly.</h3>
            <p className="mt-4 text-[15px] text-brand-gray leading-relaxed">
              The listing is often the clearest evidence a seller has of the agent's standard. A
              properly prepared listing reassures the seller that their property is being taken
              seriously — and it reduces the follow-up conversations that come from doubt.
            </p>
          </div>
          <div className="border-t-2 border-brand-blue pt-8">
            <div className="eyebrow-accent">Buyer</div>
            <h3 className="mt-3 text-2xl md:text-3xl font-bold tracking-tight">Buyers notice the standard before they know how it was created.</h3>
            <p className="mt-4 text-[15px] text-brand-gray leading-relaxed">
              Complete listings do more than look better. They make the property easier to trust.
              What buyers see online shapes the conversation before the agent ever gets a chance to
              have it.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-black text-white">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-20 md:py-28 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="eyebrow text-white/60">Next step</div>
            <h3 className="mt-3 text-2xl md:text-4xl font-bold tracking-tight">See what your listings could look like on this standard.</h3>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-brand-black px-6 py-3.5 text-[13px] font-bold">
              Request Listing Support <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/pricing" className="inline-flex items-center gap-2 border border-white/25 px-6 py-3.5 text-[13px] font-bold">
              View pricing <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
